import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';

import { ChartContainer } from '@/components/charts/ChartContainer';
import type { CodeActivityDataPoint } from '@/sections/customerManagement/types';
import { abbreviateNumber } from '@/utils/misc.util';

interface Props {
  data: CodeActivityDataPoint[];
}

const ADDITION_COLOR = '#22c55e';
const DELETION_COLOR = '#dc3232';

export function CodeActivityChart({ data }: Props) {
  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center">
        <p className="text-chariot-text-secondary text-sm">
          No code activity data available yet.
        </p>
      </div>
    );
  }

  const chartData = data.map(d => ({
    name: d.date.slice(5),
    Additions: d.additions,
    Deletions: -d.deletions,
  }));

  return (
    <div className="h-64">
      <div className="mb-2 flex flex-row-reverse justify-end gap-4">
        <div className="text-chariot-text-secondary flex items-center gap-2 text-xs">
          <div
            className="h-[6px] w-6 rounded-md"
            style={{ backgroundColor: DELETION_COLOR }}
          />
          Deletions
        </div>
        <div className="text-chariot-text-secondary flex items-center gap-2 text-xs">
          <div
            className="h-[6px] w-6 rounded-md"
            style={{ backgroundColor: ADDITION_COLOR }}
          />
          Additions
        </div>
      </div>
      <ChartContainer>
        <BarChart
          data={chartData}
          stackOffset="sign"
          margin={{ top: 4, right: 0, left: -36, bottom: 0 }}
          barSize={8}
        >
          <CartesianGrid
            strokeDasharray="0 0"
            vertical={false}
            strokeWidth={2}
            className="stroke-chariot-stroke"
          />
          <XAxis
            dataKey="name"
            className="[&_line]:stroke-chariot-stroke [&_tspan]:fill-chariot-text-secondary text-xs font-normal"
            strokeWidth={2}
            minTickGap={40}
            tickMargin={4}
          />
          <YAxis
            className="[&_tspan]:fill-chariot-text-secondary text-xs font-normal"
            axisLine={false}
            tickLine={false}
            width={72}
            tickFormatter={(value: number) =>
              abbreviateNumber(Math.abs(value)).toString()
            }
          />
          <Tooltip content={<ActivityTooltip />} cursor={false} />
          <Bar
            dataKey="Additions"
            fill={ADDITION_COLOR}
            stackId="stack"
            radius={[2, 2, 0, 0]}
          />
          <Bar
            dataKey="Deletions"
            fill={DELETION_COLOR}
            stackId="stack"
            radius={[0, 0, 2, 2]}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ActivityTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border-chariot-stroke bg-chariot-background-primary rounded-md border-2 p-2">
      <p className="text-chariot-text-primary text-xs font-bold">{label}</p>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {(payload as any[]).map((entry, i) => {
        const val = Math.abs(entry.value ?? 0);
        if (val === 0) return null;
        return (
          <p key={i} className="text-xs" style={{ color: entry.fill }}>
            {entry.name}: {val}
          </p>
        );
      })}
    </div>
  );
}
